'use client'

import { useEffect, useState } from 'react'
import { Switch } from '@/components/ui/Switch'
import { apiFetch } from '@/lib/api-fetch'

interface SettingsResponse {
  settings: {
    enabled: boolean
    handoffEnabled: boolean
    preNotificationEnabled: boolean
    preNotificationDelayMinutes: number
    updatedAt?: string
  }
  runtime: { environment: 'production' | 'preview' | 'local'; dryRun: boolean }
}

export function CourierAutomationSettings() {
  const [data, setData] = useState<SettingsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    apiFetch('/api/settings/courier-automation')
      .then(async response => {
        const body = await response.json()
        if (!response.ok) throw new Error(body.error || 'Не удалось загрузить настройку')
        if (!cancelled) setData(body)
      })
      .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Ошибка загрузки') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  async function save(settings: SettingsResponse['settings']) {
    if (!data || saving) return
    setSaving(true)
    setError('')
    try {
      const response = await apiFetch('/api/settings/courier-automation', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        // `updatedAt` is server-owned metadata. Sending the whole response back
        // violates the route's strict input contract after the first save.
        body: JSON.stringify({
          enabled: settings.enabled,
          handoffEnabled: settings.handoffEnabled,
          preNotificationEnabled: settings.preNotificationEnabled,
          preNotificationDelayMinutes: settings.preNotificationDelayMinutes,
        }),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Не удалось сохранить настройку')
      setData(body)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  async function toggle(kind: 'enabled' | 'handoffEnabled' | 'preNotificationEnabled') {
    if (!data || saving) return
    const enabled = !data.settings[kind]
    if (enabled && !window.confirm(data.runtime.dryRun
      ? 'Включить локальный тестовый режим? Сообщения в Яндекс отправляться не будут.'
      : kind === 'preNotificationEnabled'
        ? 'Включить автоотправку предуведомления? Новый оплаченный DBS-заказ получит сообщение с датой ближайшего рейса.'
        : kind === 'enabled'
        ? 'Включить автоматическую отправку? Следующее назначение курьера СДЭК отправит реальное сообщение покупателю.'
        : 'Включить автоматическую отправку? После подтверждённого приёма посылки СДЭК покупателю уйдёт сообщение с трек-номером.')) return
    await save({
      ...data.settings,
      [kind]: enabled,
    })
  }

  async function updateDelay(value: string) {
    if (!data || saving) return
    const minutes = Number(value)
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 60) {
      setError('Укажите целое число от 0 до 60 минут.')
      return
    }
    await save({ ...data.settings, preNotificationDelayMinutes: minutes })
  }

  const courierEnabled = Boolean(data?.settings.enabled)
  const handoffEnabled = Boolean(data?.settings.handoffEnabled)
  const preNotificationEnabled = Boolean(data?.settings.preNotificationEnabled)
  const preNotificationDelayMinutes = data?.settings.preNotificationDelayMinutes ?? 3
  return <section className="mb-12">
    <div className="mb-5">
      <h2 className="text-[18px] font-semibold tracking-tight text-ink">Автоматизация доставки</h2>
      <div className="mt-0.5 text-[13px] text-ink-muted">Серверная отправка уведомления после назначения курьера СДЭК</div>
    </div>
    <div className={`surface-card rounded-md p-4 sm:p-5 transition-colors ${courierEnabled || handoffEnabled || preNotificationEnabled ? 'border-gold' : ''}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-ink">Предуведомление до отправки</span>
            {data?.runtime.dryRun && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Тестовый режим</span>}
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">
            {loading ? 'Загружаем состояние…' : preNotificationEnabled
              ? data?.runtime.dryRun ? 'Включено локально: дата рейса проверяется без отправки в Яндекс.' : 'Включено: новый оплаченный DBS-заказ получит сообщение с ближайшей возможной датой отправки.'
              : 'Выключено: предуведомление можно отправить только вручную из заказа.'}
          </p>
        </div>
        <Switch checked={preNotificationEnabled} label="Автоотправка предуведомления" disabled={loading || saving || !data} onChange={() => toggle('preNotificationEnabled')} />
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 border-t border-rule pt-4">
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-ink">Пауза перед первым сообщением</div>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">После нового заказа ждём указанное время и повторно проверяем статус. По умолчанию — 3 минуты; 0 отключает паузу.</p>
        </div>
        <label className="flex shrink-0 items-center gap-2 text-[12px] text-ink-2">
          <input
            aria-label="Пауза перед первым сообщением, минут"
            className="w-16 rounded-sm border border-rule-strong bg-paper px-2 py-1 text-right text-ink disabled:opacity-50"
            defaultValue={preNotificationDelayMinutes}
            disabled={loading || saving || !data}
            max={60}
            min={0}
            onBlur={event => { void updateDelay(event.currentTarget.value) }}
            step={1}
            type="number"
          />
          мин.
        </label>
      </div>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-ink">Сообщение о курьере</span>
            {data?.runtime.dryRun && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Тестовый режим</span>}
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">
            {loading ? 'Загружаем состояние…' : courierEnabled
              ? data?.runtime.dryRun ? 'Включено локально: pipeline проверяется без отправки в Яндекс.' : 'Включено: новые назначения курьера будут отправляться автоматически.'
              : 'Выключено: сообщение можно отправить только вручную из заказа.'}
          </p>
        </div>
        <Switch checked={courierEnabled} label="Автоотправка сообщения о курьере" disabled={loading || saving || !data} onChange={() => toggle('enabled')} />
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 border-t border-rule pt-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-ink">Подтверждение передачи в СДЭК</span>
            {data?.runtime.dryRun && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Тестовый режим</span>}
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">
            {loading ? 'Загружаем состояние…' : handoffEnabled
              ? data?.runtime.dryRun ? 'Включено локально: проверяем событие приёма СДЭК без отправки в Яндекс.' : 'Включено: после подтверждённого приёма посылки СДЭК отправит покупателю трек-номер.'
              : 'Выключено: подтверждение передачи в СДЭК отправляется вручную из заказа.'}
          </p>
        </div>
        <Switch checked={handoffEnabled} label="Автоотправка подтверждения передачи в СДЭК" disabled={loading || saving || !data} onChange={() => toggle('handoffEnabled')} />
      </div>
      {error && <div role="alert" className="mt-3 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</div>}
      <div className="mt-4 grid gap-2 border-t border-rule pt-4 text-[12px] leading-relaxed text-ink-muted sm:grid-cols-2">
        <div><span className="font-semibold text-ink-2">Когда включено</span><br />Предуведомление — только для нового DBS-заказа и с учётом времени сборки. Курьерское сообщение отправляется после назначения курьера. Подтверждение передачи — только после статуса, что СДЭК принял посылку.</div>
        <div><span className="font-semibold text-ink-2">Когда выключено</span><br />Webhook продолжает обновлять доставку, но сообщение отправляется только вручную из заказа.</div>
        <div><span className="font-semibold text-ink-2">Защита от дублей</span><br />Каждая успешная отправка сохраняется на заказе. Ручная и автоматическая отправки одного типа используют общую блокировку.</div>
        <div><span className="font-semibold text-ink-2">Если Яндекс не ответил</span><br />Автоповтора нет: заказ помечается для проверки, чтобы не отправить сообщение дважды.</div>
      </div>
      {!loading && data?.runtime.environment === 'production' && <p className="mt-3 border-t border-rule pt-3 text-[11px] text-ink-faint">Production · настройка хранится на сервере и действует без открытой страницы администратора.</p>}
    </div>
  </section>
}
